import { z } from 'zod';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_CONFIG,
  RECENCY_LIMIT_DAYS,
  CRIMINAL_CHECK_FILE_TYPES
} from '../constants/documentCategories';
import {
  MAX_DOCUMENT_SIZE_BYTES,
  DOCUMENT_ACCEPTED_FILE_EXTENSIONS,
  DOCUMENT_ACCEPTED_MIME_TYPES
} from '../constants/fileUploadLimits';

export type DocumentUploadSchemaInput = {
  category: string;
  file: File | { name: string; size: number; type: string };
  certifiedAt: Date | null;
  documentVariant?: string | null;
};

export type DocumentUploadSchemaOptions = {
  today?: Date;
};

const CategoryEnum = z.enum([...DOCUMENT_CATEGORIES] as [string, ...string[]]);

const FileSchema = z
  .object({
    name: z.string().min(1, 'File name is required'),
    size: z.number().nonnegative('File must contain data'),
    type: z.string().min(1, 'File type is required')
  })
  .passthrough();

const differenceInDays = (a: Date, b: Date) => {
  const diff = Math.abs(a.getTime() - b.getTime());
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

export const createDocumentUploadSchema = (options: DocumentUploadSchemaOptions = {}) =>
  z
    .object({
      category: CategoryEnum,
      file: FileSchema,
      certifiedAt: z
        .union([z.string(), z.date()])
        .transform((value) => {
          if (value instanceof Date) {
            return value;
          }
          if (typeof value === 'string' && value.trim().length > 0) {
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
          }
          return null;
        })
        .nullable(),
      documentVariant: z
        .string()
        .trim()
        .optional()
        .nullable()
    })
    .superRefine((data, ctx) => {
      const config = DOCUMENT_CATEGORY_CONFIG[data.category as keyof typeof DOCUMENT_CATEGORY_CONFIG];
      if (!config) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['category'],
          message: 'Unknown document category'
        });
        return;
      }

      const fileName = data.file.name.toLowerCase();
      const fileExtension = '.' + (fileName.split('.').pop() || '').toLowerCase();
      if (!DOCUMENT_ACCEPTED_FILE_EXTENSIONS.includes(fileExtension as any)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['file'],
          message: `File format ${fileExtension} is not supported. Allowed formats: ${DOCUMENT_ACCEPTED_FILE_EXTENSIONS.join(
            ', '
          )}`
        });
      }

      if (!DOCUMENT_ACCEPTED_MIME_TYPES.includes(data.file.type as any)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['file'],
          message: `File type must be one of: ${DOCUMENT_ACCEPTED_MIME_TYPES.join(', ')}`
        });
      }

      if (data.file.size > MAX_DOCUMENT_SIZE_BYTES) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['file'],
          message: `File must be ${Math.round(MAX_DOCUMENT_SIZE_BYTES / (1024 * 1024))} MB or smaller`
        });
      }

      if (config.recencyRequired) {
        if (!data.certifiedAt) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['certifiedAt'],
            message: 'Certification date is required for this document'
          });
        } else {
          const today = options.today ?? new Date();
          const diffDays = differenceInDays(today, data.certifiedAt);
          if (diffDays > RECENCY_LIMIT_DAYS) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['certifiedAt'],
              message: `Document must be certified within the last ${RECENCY_LIMIT_DAYS} days`
            });
          }
        }
      }

      if (
        config.allowedFileVariants &&
        config.allowedFileVariants.length > 0 &&
        !config.allowedFileVariants.includes((data.documentVariant || '') as any)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['documentVariant'],
          message: `Select one of the accepted document types: ${CRIMINAL_CHECK_FILE_TYPES.join(', ')}`
        });
      }

      if (
        (!config.allowedFileVariants || config.allowedFileVariants.length === 0) &&
        data.documentVariant &&
        data.documentVariant.length > 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['documentVariant'],
          message: 'Document type is not applicable for this category'
        });
      }
    });

export type DocumentUploadSchema = ReturnType<typeof createDocumentUploadSchema>;
